/**
 * /it/riassunto-pdf — summary reframe bridge (Italian).
 *
 * Targets riassunto pdf (800). Reframe: per il lavoro di prodotto la decisione
 * conta più del riassunto.
 */
import { MarketingShell } from './MarketingShell';

export function ItRiassuntoPdf() {
  return (
    <MarketingShell
      currentLocale="it"
      hreflangPath="/riassunto-pdf"
      hreflangLocales={['it']}
      documentTitle="Riassunto PDF con l'AI: come fare (e cosa conta davvero) · Draftlize"
      documentDescription="Come riassumere un PDF con l'AI in pochi secondi — e perché, per il lavoro di prodotto, la decisione conta più del riassunto."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guida · Riassunto</span>
          <h1 className="serif" style={{ fontSize: 'clamp(34px, 4.6vw, 56px)', lineHeight: 1.05, margin: '12px 0 24px' }}>
            Riassumere un PDF con l'AI —<br />
            <em>e poi?</em>
          </h1>
          <p className="lede">
            Carichi un PDF lungo nell'AI e in pochi secondi hai un riassunto. Comodo. Ma sul lavoro non serve un «riepilogo breve»: serve una <em>decisione con il suo contesto</em>. Perché si è scelto così, cosa si è scartato — è proprio la parte che il riassunto taglia via. Questa pagina copre le basi del riassunto e il passo successivo: tenere traccia di ciò che si decide.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Come riassumere un PDF con l'AI.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ol style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 22 }}>
            <li><strong>Carica il documento</strong> — PDF, Word o testo lungo.</li>
            <li><strong>Genera il riassunto</strong> — uno strumento IA estrae i punti chiave (di solito con quota gratuita).</li>
            <li><strong>Verifica</strong> — controlla numeri e nomi propri.</li>
          </ol>
          <p style={{ fontSize: 17, lineHeight: 1.8, color: 'var(--ink-1)', margin: '22px 0 0' }}>
            Hai il riassunto. Ma quale <em>decisione</em> ne è uscita? Se la fonte è una riunione o un audio, prima <a href="/it/trascrizione-audio" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>trascrivi l'audio</a> e poi riassumi: stesso flusso.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">Conclusione vs conclusione con contesto</span>
            <h2 className="serif">Il riassunto è la conclusione. <em>La decisione è la conclusione con il suo perché.</em></h2>
          </div>
          <p>Il riassunto comprime «cosa c'era scritto». Il registro delle decisioni tiene aggiornato «cosa, e perché, si è deciso». Sul lavoro funziona il secondo.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th>Riassunto</th>
              <th className="ours">Registro decisioni (Draftlize)</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Cosa resta</td><td>Conclusione, punti chiave</td><td className="ours yes">Decisione + motivo + responsabile</td></tr>
            <tr><td>Il «perché»</td><td className="no">Di solito tagliato</td><td className="ours yes">Conservato nella scheda</td></tr>
            <tr><td>Quando ribalti una decisione</td><td className="no">Riassunti separati</td><td className="ours yes">Collegati e segnalati «stale»</td></tr>
            <tr><td>Settimane dopo</td><td className="partial">Non sai se è obsoleto</td><td className="ours yes">Attuale o segnalato a colpo d'occhio</td></tr>
          </tbody>
        </table>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Il riassunto conserva la «conclusione». Ma ciò che muove il lavoro è la «decisione» con il suo perché.
        </blockquote>
        <cite>Non fermarti al riassunto: tieni la decisione con il suo contesto. È Draftlize.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Domande frequenti.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: 'Come riassumo un PDF gratis?', a: 'Diversi strumenti IA offrono una quota gratuita. Draftlize non è uno strumento di riassunto: trasforma le decisioni dentro ciò che riassumi in schede che restano aggiornate.' },
            { q: 'Il riassunto AI è preciso?', a: 'Il riassunto IA è ormai valido, ma tende a tagliare il «perché». Per usarlo poi come base decisionale, va reintegrata la motivazione.' },
            { q: 'Come uso ciò che ho riassunto?', a: 'Oltre alla conclusione, salva in Draftlize la decisione che ne deriva. Se un\'altra decisione la ribalta, viene segnalata «stale» in automatico e te ne accorgi.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Non fermarti al riassunto: tieni la decisione <em>— gratis con $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>Registrati e ricevi $5</span>
          <span>Paghi solo ciò che usi</span>
          <span>Il saldo non scade mai</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Tieni il tuo strumento di riassunto. Salva in Draftlize la decisione dietro la conclusione: resta aggiornata con il suo perché, e leggibile dal tuo agente IA.
          </p>
          <a className="btn-primary" href="/">Inizia gratis con $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
